import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';

export default function AboutSection() {
  const { t } = useTranslation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCategorySelect = (cat: typeof categories[0]) => {
    setSelectedCategory(cat.name);
    setDropdownOpen(false);
    window.open(cat.url, '_blank', 'noopener,noreferrer');
  };

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-background-800 via-background-900 to-background-950"></div>
      <div className="absolute inset-0 opacity-[0.04]" style={{
        backgroundImage: 'radial-gradient(circle at 25% 25%, oklch(var(--background-50) / 0.3) 0%, transparent 50%), radial-gradient(circle at 75% 75%, oklch(var(--background-50) / 0.2) 0%, transparent 50%)'
      }}></div>

      <div className="relative z-10 w-full px-4 md:px-6 py-24 md:py-32">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
          <div className="flex-1 w-full">
            <span className="inline-block text-xs font-label font-semibold tracking-widest uppercase text-accent-300 bg-accent-500/15 px-3 py-1.5 rounded-full mb-6">
              {t('about_label')}
            </span>

            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-background-50 leading-tight mb-6">
              {t('about_title')}
            </h1>

            <p className="text-background-200/80 text-base md:text-lg leading-relaxed max-w-xl mb-8">
              {t('about_description')}
            </p>

            <div ref={dropdownRef} className="relative mb-8 max-w-md">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="w-full flex items-center justify-between bg-background-900/60 border border-background-600/40 text-background-50 rounded-lg px-5 py-3.5 text-sm font-label hover:border-background-500/60 transition-all duration-200 cursor-pointer"
              >
                <span className={selectedCategory ? 'text-background-50' : 'text-background-300'}>
                  {selectedCategory || t('select_category')}
                </span>
                {dropdownOpen ? (
                  <i className="ri-arrow-up-s-line text-background-300"></i>
                ) : (
                  <i className="ri-arrow-down-s-line text-background-300"></i>
                )}
              </button>

              {dropdownOpen && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-background-800 border border-background-600/40 rounded-lg shadow-2xl max-h-72 overflow-y-auto z-20">
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => handleCategorySelect(cat)}
                      className="w-full text-left px-5 py-3 text-sm text-background-200 hover:bg-background-700/50 hover:text-background-50 transition-colors duration-150 first:rounded-t-lg last:rounded-b-lg cursor-pointer"
                    >
                      {cat.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="bg-primary-500 text-background-50 dark:text-foreground-950 font-label font-semibold text-sm px-6 py-3 rounded-full hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                {t('cta_hoobuy_sheet')}
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="border-2 border-background-50/30 text-background-50 font-label font-semibold text-sm px-6 py-3 rounded-full hover:bg-background-50/10 hover:border-background-50/50 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                {t('cta_buy_now')}
              </a>
            </div>
          </div>

          <div className="flex-1 w-full flex flex-col lg:flex-row items-center gap-6">
            <div className="w-full lg:w-3/5 h-64 md:h-80 lg:h-96 rounded-2xl overflow-hidden bg-background-700/30">
              <img
                src="https://readdy.ai/api/search-image?query=Overhead%20flat%20lay%20of%20curated%20shopping%20items%20on%20warm%20terracotta%20textured%20surface%2C%20including%20sunglasses%20watch%20wallet%20and%20small%20notebook%2C%20natural%20sunlight%20from%20top%20left%2C%20intentional%20negative%20space%2C%20editorial%20lifestyle%20photography%2C%20Mediterranean%20warm%20color%20palette%2C%20organic%20composition%2C%20artful%20arrangement&width=800&height=960&seq=about-product-v3&orientation=portrait"
                alt="HoobuySpreadsheet products lifestyle"
                title="HoobuySpreadsheet produtos lifestyle"
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="lg:w-2/5 flex flex-col items-center lg:items-start text-center lg:text-left gap-4">
              <span className="font-heading text-6xl md:text-7xl lg:text-8xl font-bold text-background-50 leading-none">
                {t('about_stat_number')}
              </span>
              <p className="text-background-300 text-sm whitespace-pre-line leading-relaxed">
                {t('about_stat_label')}
              </p>

              <div className="flex flex-wrap gap-2 mt-2">
                {categories.slice(0, 8).map((cat) => (
                  <a
                    key={cat.id}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-xs font-label bg-background-700/40 text-background-200 px-3 py-1.5 rounded-full hover:bg-primary-500 hover:text-background-50 transition-all duration-200 whitespace-nowrap cursor-pointer"
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}